import React, { useState, useMemo, useEffect } from 'react';
import ProjectReassignModal from '../Modal/ProjectReassignModal';
import { FiEdit2, FiChevronDown, FiChevronRight, FiPlus, FiMoreVertical, FiX, FiCheck } from 'react-icons/fi';
import { usersMockData } from '@/lib/constants';
import MapNewProjectModal from './MapNewProjectModal';
import RoleChangeConfirmModal from './RoleChangeConfirmModal';
import { Tooltip } from 'react-tooltip';
import TextOverflow from 'utilities/TextOverflow';
import { getRoles, updateUserRole } from 'api/users';

interface UserData {
  id: string;
  name: string;
  email: string;
  status: string;
  startDate: string;
  endDate: string;
  invoiceDate: string;
  dueDate: string;
  organization: string;
  paymentStatus: string;
  paymentMethod: string;
  projects: Array<{
    name: string;
    role: string;
    owner: string;
    isOwner: boolean;
  }>;
  organizations: Array<{
    id: string;
    name: string;
    role: string;
    joinedDate: string;
    product: string;
    projectCount: number;
  }>;
  userType: string;
}

interface UsersTableProps {
  filters: {
    organization: string;
    status: string;
    paymentStatus: string;
    paymentMethod: string;
    subscriptionType: string;
    userType: string;
    search: string;
  }
  users: any[];
  handleRoleChange: (userId: string, newRole: string) => void;
  loadingUsers: boolean;
  type?: string;
  refreshUsers?: () => void;
}

export default function UsersTable({ filters, users, handleRoleChange, loadingUsers, type = "superadmin", refreshUsers }: UsersTableProps) {
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [showMapNewProjectModal, setShowMapNewProjectModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userProjects, setUserProjects] = useState<{[userId: string]: Array<{
    id: string;
    name: string;
    role: string;
    action: string;
  }>}>({});
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [memberRole, setMemberRole] = useState<{[projectId: string]: string}>({});
  
  // Role management state
  const [availableRoles, setAvailableRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [showRoleConfirmModal, setShowRoleConfirmModal] = useState(false);
  const [roleChangeData, setRoleChangeData] = useState<{
    userId: string;
    userName: string;
    currentRole: string;
    newRole: string;
    newRoleId: string;
  } | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [localUserRoles, setLocalUserRoles] = useState<{[userId: string]: string}>({});

  // Fetch available roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await getRoles();
        if (response.status === 200 && response.data) {
          setAvailableRoles(response.data);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
        setTimeout(() => setNotification(null), 300);
      }, 3000);
    }
  }, [notification]);

  // Handle role change request
  const handleRoleChangeRequest = (user: any, newRoleId: string, newRoleName: string) => {
    setRoleChangeData({
      userId: user.id,
      userName: user.name,
      currentRole: user.role || 'N/A',
      newRole: newRoleName,
      newRoleId: newRoleId
    });
    setShowRoleConfirmModal(true);
  };

  // Confirm and update role
  const confirmRoleChange = async () => {
    if (!roleChangeData) return;

    setIsUpdatingRole(true);
    try {
      const response = await updateUserRole({
        userId: roleChangeData.userId,
        roleId: roleChangeData.newRoleId
      });

      if (response.status === 200) {
        // Update local role state immediately
        setLocalUserRoles(prev => ({
          ...prev,
          [roleChangeData.userId]: roleChangeData.newRole
        }));
        
        setNotification('Role updated successfully!');
        setShowRoleConfirmModal(false);
        setRoleChangeData(null);
        
        // Call parent's handleRoleChange if provided
        if (handleRoleChange) {
          handleRoleChange(roleChangeData.userId, roleChangeData.newRole);
        }
        
        // Refresh users list if callback provided
        if (refreshUsers) {
          refreshUsers();
        }
      }
    } catch (error) {
      console.error('Error updating role:', error);
      setNotification('Failed to update role. Please try again.');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (userId: string) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const isRowExpanded = (userId: string) => expandedUser === userId;

  // Get user projects (with state management)
  const getUserProjects = (userId: string) => {
    // Always include default static data
    const defaultProjects = [
      {
        id: `${userId}-project-1`,
        name: "Test 1",
        role: "Owner",
        orgName: "Test Organization 1",
        action: "Remove"
      },
      {
        id: `${userId}-project-2`,
        name: "Test 2",
        role: "Admin",
        orgName: "Test Organization 2",
        action: "Remove"
      },
      {
        id: `${userId}-project-3`,
        name: "Test 3",
        role: "Developer",
        orgName: "Test Organization 3",
        action: "Remove"
      }
    ];

    // Get any additional projects that were mapped
    const additionalProjects = userProjects[userId] || [];
    
    // Return combined array: default projects + additional projects
    return [...defaultProjects, ...additionalProjects];
  };

  // Handle mapping new projects to user
  const handleMapNewProject = (userId: string) => {
    setCurrentUserId(userId);
    setShowMapNewProjectModal(true);
  };

  // Handle mapped projects from modal
  const handleMapProjects = (projects: Array<{projectName: string, role: string, organization: string}>) => {
    if (currentUserId) {
      const newProjects = projects.map((project, index) => ({
        id: `${currentUserId}-project-${Date.now()}-${index}`,
        name: project.projectName,
        role: project.role,
        orgName: project.organization,
        action: "Remove"
      }));

      setUserProjects(prev => ({
        ...prev,
        [currentUserId]: [...(prev[currentUserId] || []), ...newProjects]
      }));

      console.log(`Mapped ${projects.length} projects to user ${currentUserId}`);
    }
  };

  // Handle removing a project from user
  const handleRemoveProject = (userId: string, projectId: string) => {
    setUserProjects(prev => ({
      ...prev,
      [userId]: (prev[userId] || []).filter(project => project.id !== projectId)
    }));
  };

  // Handle Edit User action
  const handleEditUser = (userId: string) => {
    console.log('Edit user:', userId);
    // Here you would typically open an edit modal or navigate to edit page
    setOpenActionMenu(null);
  };

  // Handle View User action
  const handleViewUser = (userId: string) => {
    console.log('View user:', userId);
    // Here you would typically open a view modal or navigate to user details page
    setOpenActionMenu(null);
  };

  // Handle Delete User action
  const handleDeleteUser = (userId: string) => {
    console.log('Delete user:', userId);
    // Here you would typically show a confirmation dialog
    setOpenActionMenu(null);
  };

  // Handle Block User action
  const handleBlockUser = (userId: string) => {
    console.log('Block user:', userId);
    // Here you would typically show a confirmation dialog
    setOpenActionMenu(null);
  };

  // Toggle action menu
  const toggleActionMenu = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenActionMenu(openActionMenu === userId ? null : userId);
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openActionMenu) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openActionMenu]);

  // Helper function to get the current role value for the select
  const getCurrentRoleValue = (userId: string, userRole: string) => {
    const currentRole = localUserRoles[userId] || userRole;
    // Find matching role from available roles (case-insensitive)
    const matchingRole = availableRoles.find(
      role => role.name.toLowerCase() === currentRole?.toLowerCase()
    );
    return matchingRole ? matchingRole.name : currentRole || '';
  };


  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm) ||
        user.userType?.toLowerCase().includes(searchTerm) ||
        user.status?.toLowerCase().includes(searchTerm) ||
        user.orgName?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.organization) {
      filtered = filtered.filter(user => user.orgName === filters.organization);
    }

    if (filters.userType) {
      filtered = filtered.filter(user => user.userType === filters.userType);
    }

    if (filters.subscriptionType) {
      filtered = filtered.filter(user => user.status === filters.subscriptionType);
    }

    if (filters.paymentStatus) {
      filtered = filtered.filter(user => user.paymentStatus === filters.paymentStatus);
    }

    return filtered;
  }, [users, filters]);




  // Open the Map New User modal
  const openMapNewProjectModal = (projectId: string) => {
    setCurrentUserId(projectId);
    setShowMapNewProjectModal(true);
  };

  return (
    <>

      <div className="sticky top-[130px] bg-black z-10 pt-4">
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              {/* <th className="text-left p-4 w-[10%]">Users ID</th> */}
              <th className="text-left p-4 w-[20%]">Name</th>
              <th className="text-left p-4 w-[20%]">Email</th>
              <th className="text-left p-4 w-[10%]">Phone</th>
              <th className="text-left p-4 w-[10%]">Joined Date</th>
              <th className="text-left p-4 w-[10%]">Role</th>
              {type === "superadmin" && <th className="text-left p-4 w-[15%]">Organization</th>}
              <th className="text-left p-4 w-[10%]">Status</th>
              <th className="text-left p-4 w-[10%]">Subscription</th>
              {/* <th className="text-left p-4 w-[10%]">Status</th>
              <th className="text-left p-4 w-[10%]">Start Date</th>
              <th className="text-left p-4 w-[15%]">Payment Status</th> */}
              {/* <th className="text-left p-4 w-[10%]">Payment Method</th> */}
              <th className="text-right p-4 w-[10%]">Actions</th>
            </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden mb-[70px]">
        <table className="w-full table-fixed">
          <tbody>
            {loadingUsers ? (
              <tr>
                <td colSpan={11} className="text-center text-gray-400 p-4">
                  <div className="flex items-center justify-center space-x-2 ">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredUsers.map((user: any) => (
                <React.Fragment key={user.id}>
                  <tr
                    className={`border-b border-white/[0.08]  cursor-pointer ${
                      isRowExpanded(user.id) ? 'bg-[#cccccc33]' : ' hover:bg-[#3a3a3a33] '
                    }`}
                    onClick={() => toggleRowExpansion(user.id)}
                  >
                    {/* <td className="p-4 text-gray-400 w-[10%]">{
                      // user.id 
                      ''}</td> */}
                    <td className="p-4 w-[20%]">
                      <div className="flex items-center gap-3">
                        <button 
                          className="text-gray-400 hover:text-white transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(user.id);
                          }}
                        >
                          {isRowExpanded(user.id) ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        </button>
                        <span 
                          className="text-white" 
                          data-tooltip-id={`user-name-${user.id}`}
                          data-tooltip-content={user.name || ''}
                        >
                          {TextOverflow(user.name || '', 20)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-400 w-[20%]">
                      <span 
                        data-tooltip-id={`user-email-${user.id}`}
                        data-tooltip-content={user.email || ''}
                      >
                        {TextOverflow(user.email || '', 25)}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400 w-[10%]">{user.phone || "N/A"}</td>
                    <td className="p-4 text-gray-400 w-[10%]">{user.joinedDate || "N/A"}</td>
                    <td className="p-4 w-[10%]" onClick={(e) => e.stopPropagation()}>
                      {loadingRoles ? (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          <span>Loading...</span>
                        </div>
                      ) : (
                        <select 
                          className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white cursor-pointer hover:bg-[#3A3A3A] transition-colors"
                          value={getCurrentRoleValue(user.id, user.role)}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newRoleName = e.target.value;
                            const selectedRole = availableRoles.find(role => role.name === newRoleName);
                            const currentRoleValue = getCurrentRoleValue(user.id, user.role);
                            
                            if (selectedRole && selectedRole.name !== currentRoleValue) {
                              handleRoleChangeRequest(user, selectedRole.id, selectedRole.name);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {availableRoles.length > 0 ? (
                            availableRoles.map((role) => (
                              <option key={role.id} value={role.name}>
                                {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                              </option>
                            ))
                          ) : (
                            <option value="">No roles available</option>
                          )}
                        </select>
                      )}
                    </td>
                    {type === "superadmin" && <td className="p-4 text-gray-400 w-[15%]">{user.orgName}</td>}
                    <td className="p-4 text-gray-400 w-[10%]">{user.status} {user.status === "ACTIVE" ? <FiCheck size={16} className="text-green-400" /> : <FiX size={16} className="text-red-400" />}</td>
                    <td className="p-4 text-gray-400 w-[10%]">{user.subscription || "N/A"}</td>
                    
                    <td className="p-4 text-right w-[10%]">
                      <div className="relative">
                        <button 
                          className="text-gray-400 hover:text-white p-2 rounded transition-colors"
                          onClick={(e) => toggleActionMenu(user.id, e)}
                        >
                          <FiMoreVertical className="text-gray-400" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {openActionMenu === user.id && (
                          <div className="absolute right-0 top-full mt-1 w-40 bg-[#1A1A1A] border border-white/[0.08] rounded-md shadow-lg z-50">
                            <button
                              className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-[#3a3a3a33] hover:text-white transition-colors flex items-center gap-3 first:rounded-t-md"
                              onClick={() => handleEditUser(user.id)}
                            >
                              <FiEdit2  className="text-gray-400" />
                              Edit User
                            </button>
                            
                            <button
                              className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-[#3a3a3a33] hover:text-red-300 transition-colors flex items-center gap-3"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                fill="currentColor"
                                className="text-red-400"
                              >
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                              Delete User
                            </button>
                            <button
                              className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-[#3a3a3a33] hover:text-white transition-colors flex items-center gap-3 last:rounded-b-md"
                              onClick={() => handleBlockUser(user.id)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                fill="currentColor"
                                className="text-gray-400"
                              >
                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10z"/>
                              </svg>
                              Block User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded User Projects Section */}
                  {isRowExpanded(user.id) && (
                    <>
                      {/* User Projects Header Row */}
                      <tr className="border-b border-white/[0.08] bg-[#000]">
                        <td colSpan={11} className="p-4">
                          <div className="flex items-center justify-between">
                            <input 
                              type="text" 
                              placeholder="Search projects..." 
                              className="w-[200px] bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white" 
                              onChange={(e) => setProjectSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button 
                              className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMapNewProjectModal(user.id);
                              }}
                            >
                              <FiPlus size={16} />
                              <span className="text-sm">Map Projects</span>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* User Projects Sub-table */}
                      <tr className="bg-[#000]">
                        <td colSpan={11} className="p-0">
                          <div className="bg-[#000]">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-[#2A2A2A] text-gray-400 text-sm">
                                  <th className="text-left p-4 w-[10%]">Project Name</th>
                                  <th className="text-left p-4 w-[10%]">Started Date</th>
                                  <th className="text-left p-4 w-[10%]">Joined Date</th>
                                  <th className="text-left p-4 w-[20%]">Role in Project</th>
                                  <th className="text-left p-4 w-[10%]">Owner</th>
                                  <th className="text-left p-4 w-[10%]">Organization</th>
                                  <th className="text-right p-4 w-[10%]">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getUserProjects(user.id)
                                  .filter((project: any) => 
                                    project.name.toLowerCase().includes(projectSearch.toLowerCase())
                                  )
                                  .map((project: any) => (
                                    <tr key={project.id} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                                      <td className="p-4 w-[10%] text-white">
                                        {project.name}
                                      </td>
                                      <td className="p-4 w-[10%] text-gray-400">{project.startedDate || "N/A"}</td>
                                      <td className="p-4 w-[10%] text-gray-400">{project.joinedDate || "N/A"}</td>
                                      <td className="p-4 w-[20%] text-gray-400">
                                         <select className="w-1/2 bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                                           value={memberRole[project.id] || project.role}
                                           onChange={(e) => setMemberRole({ ...memberRole, [project.id]: e.target.value })}
                                         >
                                           <option value="owner">Owner</option>
                                           <option value="admin">Admin</option>
                                           <option value="developer">Developer</option>
                                           <option value="designer">Designer</option>
                                           <option value="viewer">Viewer</option>
                                         </select>
                                       </td>
                                       <td className="p-4 w-[10%] text-gray-400">{project.owner || "N/A"}</td>
                                       <td className="p-4 w-[10%] text-gray-400">
                                        {project.orgName}
                                      </td>
                                      <td className="p-4 text-right w-[10%]">
                                        {project.action && (
                                          <button 
                                            className="text-red-400 hover:text-red-300 underline text-sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveProject(user.id, project.id);
                                            }}
                                          >
                                            {project.action}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <ProjectReassignModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          userData={selectedUser}
          allUsers={usersMockData}
        />
      )}
      
      <MapNewProjectModal
        isOpen={showMapNewProjectModal}
        onClose={() => setShowMapNewProjectModal(false)}
        onMapProjects={handleMapProjects}
      />

      {/* Role Change Confirmation Modal */}
      {roleChangeData && (
        <RoleChangeConfirmModal
          isOpen={showRoleConfirmModal}
          onClose={() => {
            setShowRoleConfirmModal(false);
            setRoleChangeData(null);
          }}
          onConfirm={confirmRoleChange}
          userName={roleChangeData.userName}
          currentRole={roleChangeData.currentRole}
          newRole={roleChangeData.newRole}
          isLoading={isUpdatingRole}
        />
      )}

      {/* Success Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[100] transition-all duration-300 ease-out transform ${
          showNotification ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
        }`}>
          <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[300px]">
            <FiCheck className="text-2xl flex-shrink-0" />
            <span className="font-semibold text-sm drop-shadow-sm">{notification}</span>
          </div>
        </div>
      )}

      {/* Tooltips for user names and emails */}
      {filteredUsers.map((user: any) => (
        <React.Fragment key={`tooltips-${user.id}`}>
          <Tooltip
            id={`user-name-${user.id}`}
            place="top"
            className="bg-[#1A1A1A] z-50 border border-white/[0.08] !opacity-100"
            style={{
              fontSize: '0.875rem',
              padding: '0.5rem 0.75rem'
            }}
          />
          <Tooltip
            id={`user-email-${user.id}`}
            place="top"
            className="bg-[#1A1A1A] z-50 border border-white/[0.08] !opacity-100"
            style={{
              fontSize: '0.875rem',
              padding: '0.5rem 0.75rem'
            }}
          />
        </React.Fragment>
      ))}
    </>
  );
} 